import { useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAdminData } from '@/hooks/useAdminData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  BarChart3, Users, GraduationCap, Settings, DollarSign, MessageSquare, FileText,
  Calendar, Users2, Bell, TestTube, Briefcase, Gift, ShieldAlert, Award,
  Menu, LayoutDashboard, PanelLeftClose, PanelLeftOpen, ClipboardList,
} from 'lucide-react';
import { StatisticsSection } from '@/components/admin/StatisticsSection';
import { StudentsManagementSection } from '@/components/admin/StudentsManagementSection';
import { LecturersManagementSection } from '@/components/admin/LecturersManagementSection';
import { FacultyManagementSection } from '@/components/admin/FacultyManagementSection';
import { PartnersSection } from '@/components/admin/PartnersSection';
import { NewsSection } from '@/components/admin/NewsSection';
import ServicesSection from '@/components/admin/ServicesSection';
import { SettingsSection } from '@/components/admin/SettingsSection';
import { FinancialSection } from '@/components/admin/FinancialSection';
import { CommunicationSection } from '@/components/admin/CommunicationSection';
import { SystemManagementSection } from '@/components/admin/SystemManagementSection';
import { ApplicationsSection } from '@/components/admin/ApplicationsSection';
import { LecturerScheduleOverview } from '@/components/admin/LecturerScheduleOverview';
import { AdminElectronicJournal } from '@/components/admin/AdminElectronicJournal';
import { PushNotificationManagement } from '@/components/admin/PushNotificationManagement';
import { PushNotificationTest } from '@/components/admin/PushNotificationTest';
import { ImageOptimizationSection } from '@/components/admin/ImageOptimizationSection';
import { StorageManagementSection } from '@/components/admin/StorageManagementSection';
import { ServiceOrdersSection } from '@/components/admin/ServiceOrdersSection';
import { ServicePricingPackagesSection } from '@/components/admin/ServicePricingPackagesSection';
import { PortfolioProjectsSection } from '@/components/admin/PortfolioProjectsSection';
import GiftCardsManagementSection from '@/components/admin/GiftCardsManagementSection';
import { CertificatesSection } from '@/components/admin/CertificatesSection';
import { SpamMonitoringSection } from '@/components/admin/SpamMonitoringSection';

type NavItem = {
  value: string;
  label: string;
  icon: typeof BarChart3;
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    label: '\u0531\u056F\u0576\u0561\u0580\u056F',
    items: [
      { value: 'statistics', label: '\u054E\u056B\u0573\u0561\u056F\u0561\u0563\u0580\u0578\u0582\u0569\u0575\u0578\u0582\u0576', icon: BarChart3 },
      { value: 'applications', label: '\u0534\u056B\u0574\u0578\u0582\u0574\u0576\u0565\u0580', icon: FileText },
    ],
  },
  {
    label: '\u0548\u0582\u057D\u0578\u0582\u0574\u0576\u0561\u056F\u0561\u0576 \u0563\u0578\u0580\u056E\u0578\u0582\u0576\u0565\u0578\u0582\u0569\u0575\u0578\u0582\u0576',
    items: [
      { value: 'students', label: '\u0548\u0582\u057D\u0561\u0576\u0578\u0572\u0576\u0565\u0580', icon: Users },
      { value: 'lecturers', label: '\u0534\u0561\u057D\u0561\u056D\u0578\u057D\u0576\u0565\u0580', icon: GraduationCap },
      { value: 'faculties', label: '\u0556\u0561\u056F\u0578\u0582\u056C\u057F\u0565\u057F\u0576\u0565\u0580', icon: LayoutDashboard },
      { value: 'schedule', label: '\u0534\u0561\u057D\u0561\u0581\u0578\u0582\u0581\u0561\u056F', icon: Calendar },
      { value: 'certificates', label: '\u054D\u0565\u0580\u057F\u056B\u0586\u056B\u056F\u0561\u057F\u0576\u0565\u0580', icon: Award },
    ],
  },
  {
    label: '\u053F\u0561\u0575\u0584 \u0587 \u0562\u0578\u057E\u0561\u0576\u0564\u0561\u056F\u0578\u0582\u0569\u0575\u0578\u0582\u0576',
    items: [
      { value: 'news', label: '\u0546\u0578\u0580\u0578\u0582\u0569\u0575\u0578\u0582\u0576\u0576\u0565\u0580', icon: MessageSquare },
      { value: 'partners', label: '\u0533\u0578\u0580\u056E\u0568\u0576\u056F\u0565\u0580\u0576\u0565\u0580', icon: Users2 },
      { value: 'portfolio', label: '\u054A\u0578\u0580\u057F\u0586\u0578\u056C\u056B\u0578', icon: Briefcase },
    ],
  },
  {
    label: '\u053E\u0561\u057C\u0561\u0575\u0578\u0582\u0569\u0575\u0578\u0582\u0576\u0576\u0565\u0580',
    items: [
      { value: 'services', label: '\u053E\u0561\u057C\u0561\u0575\u0578\u0582\u0569\u0575\u0578\u0582\u0576\u0576\u0565\u0580', icon: Briefcase },
      { value: 'service-orders', label: '\u054A\u0561\u057F\u057E\u0565\u0580\u0576\u0565\u0580', icon: ClipboardList },
      { value: 'pricing-packages', label: '\u0533\u0576\u0561\u0575\u056B\u0576 \u0583\u0561\u0569\u0565\u0569\u0576\u0565\u0580', icon: DollarSign },
      { value: 'gift-cards', label: '\u0546\u057E\u0565\u0580 \u0584\u0561\u0580\u057F\u0565\u0580', icon: Gift },
    ],
  },
  {
    label: '\u0556\u056B\u0576\u0561\u0576\u057D\u0576\u0565\u0580 \u0587 \u0570\u0561\u0572\u0578\u0580\u0564\u0561\u056F\u0581\u0578\u0582\u0569\u0575\u0578\u0582\u0576',
    items: [
      { value: 'financial', label: '\u0556\u056B\u0576\u0561\u0576\u057D\u0576\u0565\u0580', icon: DollarSign },
      { value: 'communication', label: '\u0540\u0561\u0572\u0578\u0580\u0564\u0561\u056F\u0581\u0578\u0582\u0569\u0575\u0578\u0582\u0576', icon: MessageSquare },
      { value: 'notifications', label: '\u053E\u0561\u0576\u0578\u0582\u0581\u0578\u0582\u0574\u0576\u0565\u0580', icon: Bell },
    ],
  },
  {
    label: '\u0540\u0561\u0574\u0561\u056F\u0561\u0580\u0563',
    items: [
      { value: 'spam-monitoring', label: '\u054D\u057A\u0561\u0574 \u0574\u0578\u0576\u056B\u0569\u0578\u0580\u056B\u0576\u0563', icon: ShieldAlert },
      { value: 'settings', label: '\u053F\u0561\u0580\u0563\u0561\u057E\u0578\u0580\u0578\u0582\u0574\u0576\u0565\u0580', icon: Settings },
    ],
  },
];

const AdminDashboard = () => {
  const { user } = useAuth();
  const { registrations, updateRegistrationStatus, deleteRegistration, getSetting, updateSetting, loading } = useAdminData();
  const [activeTab, setActiveTab] = useState('statistics');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeItem = useMemo(
    () => NAV_GROUPS.flatMap((group) => group.items).find((item) => item.value === activeTab),
    [activeTab],
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        {'\u0532\u0565\u057C\u0576\u057E\u0578\u0582\u0574 \u0567...'}
      </div>
    );
  }

  const renderNav = (onNavigate?: () => void) => (
    <nav className="space-y-6 p-3">
      {NAV_GROUPS.map((group) => (
        <div key={group.label}>
          {!collapsed && (
            <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </p>
          )}
          <div className="space-y-1">
            {group.items.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.value;
              return (
                <button
                  key={item.value}
                  type="button"
                  title={item.label}
                  onClick={() => {
                    setActiveTab(item.value);
                    onNavigate?.();
                  }}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    collapsed && 'justify-center px-2',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex">
        {/* Desktop sidebar */}
        <aside
          className={cn(
            'sticky top-[72px] hidden h-[calc(100vh-72px)] shrink-0 border-r border-border/70 bg-card/60 backdrop-blur-sm lg:block',
            collapsed ? 'w-[76px]' : 'w-64',
          )}
        >
          <div className="flex h-14 items-center justify-between gap-2 border-b border-border/70 px-3">
            {!collapsed && (
              <span className="truncate text-sm font-semibold">
                {'\u0531\u0564\u0574\u056B\u0576 \u057E\u0561\u0570\u0561\u0576\u0561\u056F'}
              </span>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="cursor-pointer"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={() => setCollapsed((v) => !v)}
            >
              {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </Button>
          </div>
          <ScrollArea className="h-[calc(100%-3.5rem)]">{renderNav()}</ScrollArea>
        </aside>

        {/* Main */}
        <div className="min-w-0 flex-1">
          <div className="sticky top-[72px] z-20 flex items-center gap-3 border-b border-border/70 bg-background/85 px-4 py-3 backdrop-blur-sm">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="cursor-pointer lg:hidden" aria-label="Menu">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <ScrollArea className="h-full pt-8">{renderNav(() => setMobileOpen(false))}</ScrollArea>
              </SheetContent>
            </Sheet>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold sm:text-xl">
                {activeItem?.label ?? '\u0531\u0564\u0574\u056B\u0576 \u057E\u0561\u0570\u0561\u0576\u0561\u056F'}
              </h1>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="mx-auto w-full max-w-[1400px]">
              {activeTab === 'statistics' && <StatisticsSection />}
              {activeTab === 'applications' && (
                <ApplicationsSection
                  registrations={registrations}
                  updateRegistrationStatus={updateRegistrationStatus}
                  deleteRegistration={deleteRegistration}
                />
              )}
              {activeTab === 'students' && <StudentsManagementSection />}
              {activeTab === 'lecturers' && <LecturersManagementSection />}
              {activeTab === 'faculties' && <FacultyManagementSection />}
              {activeTab === 'certificates' && <CertificatesSection />}
              {activeTab === 'news' && <NewsSection />}
              {activeTab === 'partners' && <PartnersSection />}
              {activeTab === 'portfolio' && <PortfolioProjectsSection />}
              {activeTab === 'services' && <ServicesSection />}
              {activeTab === 'service-orders' && <ServiceOrdersSection />}
              {activeTab === 'pricing-packages' && <ServicePricingPackagesSection />}
              {activeTab === 'gift-cards' && <GiftCardsManagementSection />}
              {activeTab === 'financial' && <FinancialSection />}
              {activeTab === 'communication' && <CommunicationSection />}
              {activeTab === 'spam-monitoring' && <SpamMonitoringSection />}

              {activeTab === 'schedule' && (
                <Tabs defaultValue="schedule-overview" className="w-full">
                  <TabsList className="mb-6 grid w-full max-w-xl grid-cols-2">
                    <TabsTrigger value="schedule-overview" className="cursor-pointer">
                      <Calendar className="mr-2 h-4 w-4" />
                      {'\u0534\u0561\u057D\u0561\u0581\u0578\u0582\u0581\u0561\u056F'}
                    </TabsTrigger>
                    <TabsTrigger value="electronic-journal" className="cursor-pointer">
                      <FileText className="mr-2 h-4 w-4" />
                      {'\u0537\u056C. \u0574\u0561\u057F\u0575\u0561\u0576'}
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="schedule-overview"><LecturerScheduleOverview /></TabsContent>
                  <TabsContent value="electronic-journal"><AdminElectronicJournal /></TabsContent>
                </Tabs>
              )}

              {activeTab === 'notifications' && (
                  <Tabs defaultValue="push-management" className="w-full">
                    <div className="mb-6 min-w-0">
                      <div className="-mx-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <TabsList className="inline-flex h-auto min-w-max border-border/70 bg-muted/70 p-1.5 sm:grid sm:w-full sm:max-w-xl sm:min-w-0 sm:grid-cols-2">
                          <TabsTrigger value="push-management" className="min-h-11 shrink-0 rounded-md px-3 text-xs cursor-pointer text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
                            <Bell className="mr-2 h-4 w-4" />Push
                          </TabsTrigger>
                          <TabsTrigger value="push-test" className="min-h-11 shrink-0 rounded-md px-3 text-xs cursor-pointer text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
                            <TestTube className="mr-2 h-4 w-4" />Push {'\u0569\u0565\u057D\u057F'}
                          </TabsTrigger>
                        </TabsList>
                      </div>
                    </div>
                  <TabsContent value="push-management"><PushNotificationManagement /></TabsContent>
                  <TabsContent value="push-test"><PushNotificationTest /></TabsContent>
                </Tabs>
              )}

              {activeTab === 'settings' && (
                <div className="space-y-6">
                  <SettingsSection getSetting={getSetting} updateSetting={updateSetting} />
                  <Separator />
                  <StorageManagementSection />
                  <ImageOptimizationSection />
                  <SystemManagementSection />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
